/**
 * タイムライン（docs/01-requirements.md §5.6）。起動画面になり得るビューの 1 つ。
 *
 * 設計上の要点:
 * - 全ノートを対象日基準で降順に並べる折りたたみ式フィード。
 * - 既定の開閉は `isRecentForTimeline` に委ねる（直近3日以内は展開）。
 *   ユーザーが手で開閉した結果はこの画面にいる間だけ保持する。
 * - 取得・ページング・無限スクロールは `useNoteFeed`、絞り込みの記憶は
 *   `view-preference` と共有する（一覧ビューと状態を揃えるため。ADR-030）。
 * - 起動時に未送信ドラフトを掃き出す（何も失わない保証の最後の受け皿。§5.1 P0）。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { ErrorNotice } from '@app/components/ErrorNotice';
import { Icon } from '@app/components/icons';
import { MarkdownView } from '@app/components/MarkdownView';
import { NoteFilterBar } from '@app/components/NoteFilterBar';
import { subjectLabel } from '@app/components/subject-label';
import { useCategories } from '@app/components/useCategories';
import { ViewSwitcher } from '@app/components/ViewSwitcher';
import { useNoteFeed } from '@app/hooks/useNoteFeed';
import { useStartupMaintenance } from '@app/save/useStartupMaintenance';
import {
  loadViewState,
  saveViewState,
  type ViewState,
} from '@app/views/view-preference';
import { isRecentForTimeline } from '@shared/date';
import { deriveDisplayTitle } from '@shared/markdown-input';
import type { NoteSummary } from '@shared/types';

/** 1 ページの件数。スマホの初期表示を軽くするため控えめにする。 */
const PAGE_SIZE = 20;
/** 折りたたみ時に見せる本文の行数。 */
const COLLAPSED_LINES = 3;

export function TimelinePage() {
  const categories = useCategories();

  const [view, setView] = useState<ViewState>(loadViewState);
  /** ユーザーが手で開閉したノート。既定ルールより優先する。 */
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const { notes, loading, done, error, reload, sentinelRef } = useNoteFeed(view, PAGE_SIZE);

  useStartupMaintenance();

  const updateView = useCallback((patch: Partial<ViewState>) => {
    setView((prev) => {
      const next = { ...prev, ...patch };
      saveViewState(next);
      return next;
    });
  }, []);

  const toggle = useCallback((id: string, next: boolean) => {
    setOverrides((prev) => ({ ...prev, [id]: next }));
  }, []);

  // 既定の開閉判定の基準時刻。スクロール中に日付が変わって畳み方が揺れないよう固定する。
  const now = useMemo(() => Date.now(), []);

  return (
    <div className="page">
      <AppHeader
        title={<ViewSwitcher current="timeline" />}
        right={
          <nav className="app-header__nav" aria-label="移動">
            <Link className="icon-button" to="/search" aria-label="検索" title="検索">
              <Icon name="search" />
            </Link>
            <Link className="icon-button" to="/trash" aria-label="ゴミ箱" title="ゴミ箱">
              <Icon name="trash" />
            </Link>
            <Link className="icon-button" to="/settings" aria-label="設定" title="設定">
              <Icon name="settings" />
            </Link>
          </nav>
        }
      />

      <NoteFilterBar view={view} onChange={updateView} />

      <main className="page__body page__body--timeline">
        {error != null && <ErrorNotice error={error} onRetry={reload} />}

        {notes.length === 0 && !loading && error == null && (
          <div className="empty-state">
            <p>まだノートがありません。</p>
            <p className="muted">思いついたことを、そのまま書き始められます。</p>
            <Link className="al-button al-button--primary" to="/notes/new">
              最初のノートを書く
            </Link>
          </div>
        )}

        <ul className="timeline">
          {notes.map((note) => (
            <TimelineItem
              key={note.id}
              summary={note}
              categoryName={categories.nameOf(note.category_id)}
              expanded={
                overrides[note.id] ??
                isRecentForTimeline(note.subject_date, note.subject_date_end, now)
              }
              onToggle={toggle}
            />
          ))}
        </ul>

        {loading && <p className="muted centered-note">読み込み中…</p>}
        {!done && <div ref={sentinelRef} className="timeline__sentinel" aria-hidden="true" />}
        {done && notes.length > 0 && <p className="muted centered-note">ここまでです</p>}
      </main>

      <Link
        className="fab"
        to="/notes/new"
        aria-label="新しいノートを書く"
        title="新しいノートを書く"
      >
        <Icon name="plus" size={26} />
      </Link>
    </div>
  );
}

interface TimelineItemProps {
  summary: NoteSummary;
  categoryName: string | null;
  expanded: boolean;
  onToggle: (id: string, next: boolean) => void;
}

function TimelineItem({ summary, categoryName, expanded, onToggle }: TimelineItemProps) {
  /** 展開時に取得した全文。抜粋が全文と一致する（truncated=false）なら取得しない。 */
  const [fullBody, setFullBody] = useState<string | null>(
    summary.truncated ? null : summary.excerpt,
  );
  const [bodyError, setBodyError] = useState(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!expanded || fullBody !== null || fetchingRef.current) return;
    fetchingRef.current = true;
    let cancelled = false;
    api
      .getNote(summary.id)
      .then((note) => {
        if (!cancelled) setFullBody(note.body);
      })
      .catch(() => {
        // 全文が取れなくても、抜粋は読める状態を保つ。
        if (!cancelled) {
          setBodyError(true);
          setFullBody(summary.excerpt);
        }
      })
      .finally(() => {
        fetchingRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, fullBody, summary.id, summary.excerpt]);

  const title = deriveDisplayTitle(summary.title, summary.excerpt);

  return (
    <li className="note-card">
      <div className="note-card__meta">
        <span className="note-card__date">
          {subjectLabel(summary.subject_date, summary.subject_date_end)}
        </span>
        {categoryName && (
          <span className="al-chip al-chip--lilac mm-category-badge">{categoryName}</span>
        )}
      </div>

      <div className="note-card__head">
        <button
          type="button"
          className="note-card__toggle"
          aria-expanded={expanded}
          onClick={() => onToggle(summary.id, !expanded)}
        >
          <span className="note-card__caret">
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={16} />
          </span>
          <span className="note-card__title">{title}</span>
        </button>
        <Link
          className="al-button al-button--secondary note-card__edit"
          to={`/notes/${summary.id}/edit`}
        >
          編集
        </Link>
      </div>

      {expanded ? (
        fullBody === null ? (
          <p className="muted">本文を読み込み中…</p>
        ) : (
          <>
            <MarkdownView source={fullBody} />
            {bodyError && (
              <p className="muted">全文を取得できませんでした（抜粋を表示しています）</p>
            )}
          </>
        )
      ) : (
        <div className="note-card__excerpt">
          <MarkdownView source={summary.excerpt} maxLines={COLLAPSED_LINES} />
        </div>
      )}
    </li>
  );
}
